import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Form, Badge, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState, getReportColumns } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import MonthlyProfitabilityGuideModal from '../components/MonthlyProfitabilityGuideModal'
import '../styles/selectControl.css'

const fmtDec = (val, digits = 2) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(digits)
}

const fmtPct = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(1) + '%'
}

const fmtInt = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toLocaleString()
}

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
]

function getDefaultYearMonth() {
    const now = new Date()
    let year = now.getFullYear()
    let month = now.getMonth() // 0-based, so this is previous month (Jan=0 means Dec of prev year)
    if (month === 0) {
        month = 12
        year = year - 1
    }
    return { year, month }
}

function generateYearOptions() {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let y = 2024; y <= currentYear; y++) {
        years.push(y)
    }
    return years
}

function AmazonMonthlyProfitabilityScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)
    const defaults = getDefaultYearMonth()

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [selectedYear, setSelectedYear] = useState(() => parseInt(urlParams.get('year')) || defaults.year)
    const [selectedMonth, setSelectedMonth] = useState(() => parseInt(urlParams.get('month')) || defaults.month)
    const [error, setError] = useState('')

    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [summary, setSummary] = useState(null)
    const [dataSource, setDataSource] = useState('')
    const [collecting, setCollecting] = useState(false)
    const [collectAlert, setCollectAlert] = useState(null)
    const [collectionStatus, setCollectionStatus] = useState(null)
    const collectionPollRef = useRef(null)

    const [showGuide, setShowGuide] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [excludeInactive, setExcludeInactive] = useState(true)
    const [searchTerm, setSearchTerm] = useState(() => urlParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(() => urlParams.get('search') || '')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(() => urlParams.get('sortCol') || 'revenue')
    const [sortDirection, setSortDirection] = useState(() => urlParams.get('sortDir') || 'desc')
    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            asin: true, amazon_sku: true, product_name: true, sale_price: true,
            fba_fee: true, inbound_fee: true, referral_fee: true,
            storage_fee: true, awd_fee: true, deal_fee: true, cogs: true, profit: true,
            contribution_margin: true, break_even_acos: true, max_profitable_cpa: true,
            return_on_cost: true,
            units_sold: true, units_returned: true, revenue: true,
            inventory_level: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_monthly_profitability', initialTableState)
    const columnVisibility = { ...initialTableState.visibility, ...(columnState.visibility || {}) }
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        asin: 'ASIN', amazon_sku: 'SKU', product_name: 'Product Name',
        sale_price: 'Sale Price', fba_fee: 'FBA Fee', inbound_fee: 'Inbound Fee',
        referral_fee: 'Referral Fee', storage_fee: 'Storage Fee', awd_fee: 'AWD Fee',
        deal_fee: 'Deal Fee', cogs: 'COGS', profit: 'Profit',
        contribution_margin: 'Margin %', break_even_acos: 'BE ACoS %',
        max_profitable_cpa: 'Max CPA', return_on_cost: 'Return on Cost %',
        units_sold: 'Units Sold', units_returned: 'Units Returned',
        revenue: 'Revenue', inventory_level: 'Inventory Level',
    }

    // Mount-only: restore filters from returnFilters
    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    // Sync filters to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (selectedYear) params.set('year', selectedYear)
        if (selectedMonth) params.set('month', selectedMonth)
        if (searchTerm) params.set('search', searchTerm)
        if (sortColumn !== 'revenue') params.set('sortCol', sortColumn)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (currentPage > 1) params.set('page', currentPage)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }, [selectedAccountId, selectedYear, selectedMonth, searchTerm, sortColumn, sortDirection, currentPage])

    // Fetch accounts on mount
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                if (data) {
                    const active = data.filter(a => a.is_active)
                    setAccounts(active)
                    if (active.length === 1 && !selectedAccountId) {
                        setSelectedAccountId(active[0].id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch accounts:', err)
            }
        }
        fetchAccounts()
    }, [])

    const fetchData = useCallback(async () => {
        if (!selectedAccountId) return
        setLoading(true)
        setError('')
        try {
            const params = {
                account_id: selectedAccountId,
                year: selectedYear,
                month: selectedMonth,
                page: currentPage,
                pageSize: pageSize,
                sortColumn: sortColumn,
                sortDirection: sortDirection,
            }
            if (searchTerm) params.searchTerm = searchTerm
            params.exclude_inactive = excludeInactive

            const data = await amazonAPI.monthlyProfitability.get(params)
            if (data) {
                setRows(data.results || [])
                setTotalItems(data.count || 0)
                setSummary(data.summary || null)
                setDataSource(data.data_source || '')
                setDataLoaded(true)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [selectedAccountId, selectedYear, selectedMonth, currentPage, pageSize, sortColumn, sortDirection, searchTerm, excludeInactive])

    const handleLoadReport = () => {
        setCurrentPage(1)
        fetchData()
    }

    // Re-fetch when pagination, sort, or search changes (but only after initial load)
    useEffect(() => {
        if (dataLoaded) {
            fetchData()
        }
    }, [currentPage, pageSize, sortColumn, sortDirection, searchTerm, excludeInactive])

    const handleSearch = (e) => {
        const value = e?.target?.value ?? e
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
            setCurrentPage(1)
        }, 300)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const stopPolling = useCallback(() => {
        if (collectionPollRef.current) {
            clearInterval(collectionPollRef.current)
            collectionPollRef.current = null
        }
    }, [])

    const fetchCollectionStatus = useCallback(async () => {
        if (!selectedAccountId) return
        const result = await amazonAPI.monthlyCollection.getStatus(selectedAccountId, {
            year: selectedYear, month: selectedMonth,
        })
        if (result) {
            setCollectionStatus(result)
            if (['DONE', 'FAILED', 'IDLE'].includes(result.overall_status)) {
                stopPolling()
                if (result.overall_status === 'DONE') {
                    fetchData()
                }
            }
        }
    }, [selectedAccountId, selectedYear, selectedMonth, fetchData, stopPolling])

    const fetchStatusRef = useRef(fetchCollectionStatus)
    fetchStatusRef.current = fetchCollectionStatus

    const startPolling = useCallback(() => {
        if (collectionPollRef.current) clearInterval(collectionPollRef.current)
        collectionPollRef.current = setInterval(() => {
            fetchStatusRef.current()
        }, 15000)
    }, [])

    useEffect(() => {
        return () => stopPolling()
    }, [stopPolling])

    useEffect(() => {
        if (dataLoaded && selectedAccountId) {
            fetchCollectionStatus()
        }
    }, [dataSource, selectedAccountId, selectedYear, selectedMonth])

    useEffect(() => {
        if (collectionStatus && ['PROCESSING', 'PENDING'].includes(collectionStatus.overall_status)) {
            if (!collectionPollRef.current) {
                startPolling()
            }
        }
    }, [collectionStatus, startPolling])

    const handleCollectMonthly = async () => {
        if (!selectedAccountId) return
        setCollecting(true)
        setCollectAlert(null)
        try {
            const result = await amazonAPI.monthlyCollection.trigger(selectedAccountId, {
                year: selectedYear, month: selectedMonth,
            })
            if (result) {
                if (result.already_exists) {
                    setCollectAlert({ variant: 'warning', message: result.error })
                } else {
                    setCollectAlert({ variant: 'success', message: result.message })
                    fetchCollectionStatus()
                    startPolling()
                }
            }
        } catch (err) {
            setCollectAlert({ variant: 'danger', message: formatErrorResponse(err) })
        } finally {
            setCollecting(false)
        }
    }

    const handleRetryFailed = async () => {
        if (!selectedAccountId) return
        setCollecting(true)
        setCollectAlert(null)
        try {
            const result = await amazonAPI.monthlyCollection.retry(selectedAccountId, {
                year: selectedYear, month: selectedMonth,
            })
            if (result) {
                setCollectAlert({ variant: 'success', message: result.message })
                fetchCollectionStatus()
                startPolling()
            }
        } catch (err) {
            setCollectAlert({ variant: 'danger', message: formatErrorResponse(err) })
        } finally {
            setCollecting(false)
        }
    }

    const stepDotStyle = (stepStatus) => {
        const colors = { DONE: '#198754', PROCESSING: '#ffc107', SUBMITTED: '#ffc107', FAILED: '#dc3545' }
        return {
            width: 22, height: 22, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.65rem',
            color: stepStatus === 'PROCESSING' || stepStatus === 'SUBMITTED' ? '#000' : '#fff',
            background: colors[stepStatus] || '#adb5bd',
        }
    }

    const stepConnectorStyle = (stepStatus) => ({
        width: 16, height: 2,
        background: stepStatus === 'DONE' ? '#198754' : 'var(--bs-border-color)',
        display: 'inline-block', verticalAlign: 'middle',
    })

    const stepDotContent = (stepStatus) => {
        if (stepStatus === 'DONE') return <i className="bi bi-check" />
        if (stepStatus === 'FAILED') return <i className="bi bi-x" />
        if (stepStatus === 'PROCESSING' || stepStatus === 'SUBMITTED')
            return <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 2 }} />
        return <i className="bi bi-clock" style={{ fontSize: '0.55rem' }} />
    }

    const stepStatusLabel = (stepStatus) => {
        if (stepStatus === 'DONE') return 'Complete'
        if (stepStatus === 'FAILED') return 'Failed'
        if (stepStatus === 'PROCESSING' || stepStatus === 'SUBMITTED') return 'In progress'
        return stepStatus || 'Pending'
    }

    const renderCollectionStepIndicator = () => {
        if (!collectionStatus || !collectionStatus.steps || collectionStatus.overall_status === 'IDLE') return null
        const steps = [
            { label: 'Economics', ...(collectionStatus.steps.economics || {}) },
            { label: 'Traffic', ...(collectionStatus.steps.traffic || {}) },
            { label: 'Ads', ...(collectionStatus.steps.ad_performance || {}) },
        ]
        return (
            <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', paddingBottom: 16, marginLeft: 8 }}>
                {steps.map((step, i) => (
                    <React.Fragment key={step.label}>
                        {i > 0 && <div style={stepConnectorStyle(steps[i - 1].status)} />}
                        <OverlayTrigger placement="top" overlay={
                            <Tooltip>
                                {step.label}: {stepStatusLabel(step.status)}
                                {step.status === 'FAILED' && step.error && (
                                    <div style={{ fontSize: '0.75rem', marginTop: 2 }}>{step.error}</div>
                                )}
                            </Tooltip>
                        }>
                            <div style={{ ...stepDotStyle(step.status), position: 'relative', cursor: 'default' }}>
                                {stepDotContent(step.status)}
                                <span style={{
                                    position: 'absolute', top: 24, fontSize: '0.6rem',
                                    color: 'var(--bs-secondary-color)', whiteSpace: 'nowrap',
                                    left: '50%', transform: 'translateX(-50%)',
                                }}>{step.label}</span>
                            </div>
                        </OverlayTrigger>
                    </React.Fragment>
                ))}
            </div>
        )
    }

    const handleSort = (column, direction) => {
        setSortColumn(column)
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const handleExport = async () => {
        setExporting(true)
        setError('')
        try {
            const columns = getReportColumns('amazon_monthly_profitability', columnVisibility)
            await amazonAPI.monthlyProfitability.exportExcel({
                account_id: selectedAccountId,
                year: selectedYear,
                month: selectedMonth,
                exclude_inactive: excludeInactive,
                searchTerm,
                sortColumn,
                sortDirection,
                columns,
            })
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setExporting(false) }
    }

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))
    const yearOptions = generateYearOptions()

    const reportColumns = [
        { id: 'asin', header: 'ASIN', sortable: true,
          cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        { id: 'amazon_sku', header: 'SKU', sortable: true,
          cell: (row) => row.amazon_sku || '' },
        { id: 'product_name', header: 'Product Name', sortable: true,
          cell: (row) => row.product_name || '' },
        { id: 'sale_price', header: 'Sale Price', sortable: true,
          cell: (row) => row.sale_price != null ? `$${fmtDec(row.sale_price)}` : '' },
        { id: 'fba_fee', header: 'FBA Fee', sortable: true,
          cell: (row) => row.fba_fee != null ? `$${fmtDec(row.fba_fee)}` : '' },
        { id: 'inbound_fee', header: 'Inbound Fee', sortable: true,
          cell: (row) => row.inbound_fee != null ? `$${fmtDec(row.inbound_fee)}` : '' },
        { id: 'referral_fee', header: 'Referral Fee', sortable: true,
          cell: (row) => row.referral_fee != null ? `$${fmtDec(row.referral_fee)}` : '' },
        { id: 'storage_fee', header: 'Storage Fee', sortable: true,
          cell: (row) => row.storage_fee != null ? `$${fmtDec(row.storage_fee)}` : '' },
        { id: 'awd_fee', header: 'AWD Fee', sortable: true,
          cell: (row) => row.awd_fee != null ? `$${fmtDec(row.awd_fee)}` : '' },
        { id: 'deal_fee', header: 'Deal Fee', sortable: true,
          cell: (row) => row.deal_fee != null ? `$${fmtDec(row.deal_fee)}` : '' },
        { id: 'cogs', header: 'COGS', sortable: true,
          cell: (row) => {
              if (row.cogs === null || row.cogs === undefined || Number(row.cogs) === 0) {
                  return row.units_sold > 0
                      ? <i className="fas fa-exclamation-triangle text-warning" title="COGS missing" style={{ fontSize: '0.75rem' }}></i>
                      : ''
              }
              return `$${fmtDec(row.cogs)}`
          }},
        { id: 'profit', header: 'Profit', sortable: true,
          cell: (row) => {
              if (row.profit === null || row.profit === undefined) return ''
              const val = Number(row.profit)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>${fmtDec(val)}</span>
          }},
        { id: 'contribution_margin', header: 'Margin %', sortable: true,
          cell: (row) => {
              if (row.contribution_margin === null || row.contribution_margin === undefined) return ''
              const val = Number(row.contribution_margin)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>{fmtPct(val)}</span>
          }},
        { id: 'break_even_acos', header: 'BE ACoS %', sortable: true,
          cell: (row) => row.break_even_acos != null ? fmtPct(Number(row.break_even_acos)) : '' },
        { id: 'max_profitable_cpa', header: 'Max CPA', sortable: true,
          cell: (row) => row.max_profitable_cpa != null ? `$${fmtDec(row.max_profitable_cpa)}` : '' },
        { id: 'return_on_cost', header: 'Return on Cost %', sortable: true,
          cell: (row) => row.return_on_cost != null ? fmtPct(Number(row.return_on_cost)) : '' },
        { id: 'units_sold', header: 'Units Sold', sortable: true,
          cell: (row) => fmtInt(row.units_sold) },
        { id: 'units_returned', header: 'Units Returned', sortable: true,
          cell: (row) => fmtInt(row.units_returned) },
        { id: 'revenue', header: 'Revenue', sortable: true,
          cell: (row) => row.revenue != null ? `$${fmtDec(row.revenue)}` : '' },
        { id: 'inventory_level', header: 'Inventory Level', sortable: true,
          cell: (row) => {
              if (!row.inventory_level) return ''
              const variantMap = { 'Out of stock': 'danger', 'Low stock': 'warning', 'Enough': 'success' }
              const variant = variantMap[row.inventory_level] || 'secondary'
              return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{row.inventory_level}</Badge>
          }},
    ].filter(col => columnVisibility[col.id])

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="mb-0">Monthly Profitability</h1>
                <Button variant="outline-primary" size="sm" onClick={() => setShowGuide(true)}>
                    <i className="fas fa-info-circle me-1"></i>Column Guide
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-3">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => setSelectedAccountId(opt?.value || '')}
                            options={accountOptions}
                            isClearable
                            placeholder="Select Account..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '120px' }}>
                        <Form.Label>Year</Form.Label>
                        <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                            {yearOptions.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </Form.Select>
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>Month</Form.Label>
                        <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
                            {MONTHS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </Form.Select>
                    </div>
                    <div>
                        <Button variant="primary" onClick={handleLoadReport}
                            disabled={loading || !selectedAccountId}>
                            {loading ? (<><Spinner size="sm" className="me-2" />Loading...</>) : 'Load Report'}
                        </Button>
                    </div>
                </div>
                <Form.Check type="checkbox" label="Exclude items with no activity"
                    checked={excludeInactive} onChange={(e) => { setExcludeInactive(e.target.checked); setCurrentPage(1) }}
                    style={{ fontSize: '0.85rem' }} />
            </div>

            {dataLoaded && (
                <>
                    {dataSource && (
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <Badge bg={dataSource === 'monthly' ? 'success' : 'warning'}
                                   text={dataSource === 'monthly' ? undefined : 'dark'}>
                                {dataSource === 'monthly' ? 'Monthly Data' : 'Daily Aggregated'}
                            </Badge>
                            {dataSource === 'daily_aggregated' && hasPermission('amazon.manage') && (
                                <Button variant="outline-primary" size="sm"
                                    onClick={handleCollectMonthly} disabled={collecting}>
                                    {collecting ? (<><Spinner size="sm" className="me-1" />Collecting...</>) : (
                                        <><i className="fas fa-download me-1"></i>Collect Monthly Data</>
                                    )}
                                </Button>
                            )}
                            {collectionStatus?.overall_status === 'FAILED' && hasPermission('amazon.manage') && (
                                <Button variant="outline-warning" size="sm"
                                    onClick={handleRetryFailed} disabled={collecting}>
                                    <i className="fas fa-redo me-1"></i>Retry Failed
                                </Button>
                            )}
                            {collectAlert && (
                                <span className={`small text-${collectAlert.variant}`}>{collectAlert.message}</span>
                            )}
                            {renderCollectionStepIndicator()}
                        </div>
                    )}

                    {summary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total ASINs</h5><h3 className="mb-0">{summary.total_asins || 0}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Revenue</h5><h3 className="mb-0">{formatCurrency(summary.total_revenue)}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Units Sold</h5><h3 className="mb-0">{(summary.total_units_sold || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Avg Margin</h5><h3 className="mb-0">{summary.avg_margin != null ? `${Number(summary.avg_margin).toFixed(1)}%` : ''}</h3></div></Col>
                        </Row>
                    )}

                    <div className="d-flex align-items-center mb-3">
                        <Button variant="info" onClick={handleExport} disabled={exporting || !dataLoaded} className="me-2">
                            <i className="bi bi-file-earmark-excel me-2"></i>{exporting ? 'Exporting...' : 'Export Excel'}
                        </Button>
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search ASIN, name, or SKU..." />
                        </div>
                    </div>

                    {loading && rows.length === 0 ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : rows.length === 0 ? (
                        <p className="text-muted">No profitability data found for the selected period.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={reportColumns} data={rows} tableId="amazon_monthly_profitability"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        asin: 130, amazon_sku: 110, product_name: 250,
                                        sale_price: 90, fba_fee: 90, inbound_fee: 90,
                                        referral_fee: 100, storage_fee: 90, awd_fee: 80,
                                        deal_fee: 80, cogs: 70, profit: 80, contribution_margin: 100,
                                        break_even_acos: 95, max_profitable_cpa: 100,
                                        return_on_cost: 120,
                                        units_sold: 90, units_returned: 110, revenue: 100,
                                        inventory_level: 130,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={setPageSize}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
            <MonthlyProfitabilityGuideModal show={showGuide} onHide={() => setShowGuide(false)} />
        </Container>
    )
}

export default AmazonMonthlyProfitabilityScreen
